import { useState } from "react";
import { View, ScrollView } from "react-native";
import { ShoppingBag, Check } from "lucide-react-native";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useStytch } from "@src/packages/api/useStytch";
import { getIdsFromMetadata } from "@src/packages/api/louise-api";
import { useTranslations } from "@src/packages/i18n";
import { fetchTestingCheckoutSecret } from "../hooks/testingCheckoutApi";
import { EMBER } from "../../onboarding/theme";
import {
  CloseButton,
  Dialog,
  DialogActions,
  PrimaryButton,
  SecondaryButton,
  FieldMessage,
} from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";

/**
 * Lazily load Stripe.js. Calling loadStripe() injects the js.stripe.com script
 * and fires Stripe's m.stripe.com telemetry beacon, so we hold off until the
 * user actually starts a checkout instead of doing it at module load. Memoized
 * so repeat checkouts reuse the same instance.
 */
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = loadStripe(
      process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? "",
    );
  }
  return stripePromise;
}

/** The pay form rendered inside <Elements>; confirms the PaymentIntent in-page. */
function WebCheckoutForm({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: () => void;
}) {
  const t = useTranslations();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  return (
    <>
      {/* Card fields scroll; the Order button below stays pinned and visible. */}
      <ScrollView
        contentContainerStyle={{ paddingBottom: 16 }}
        showsVerticalScrollIndicator
      >
        <PaymentElement />
      </ScrollView>
      <DialogActions>
        <PrimaryButton
          label={t("learn.test_detail.order_now")}
          action="learn.test_detail.confirm_order"
          loading={submitting}
          disabled={!stripe || !elements}
          onPress={async () => {
            if (!stripe || !elements) return;
            setSubmitting(true);
            const { error } = await stripe.confirmPayment({
              elements,
              redirect: "if_required",
            });
            setSubmitting(false);
            if (error) onError();
            else onSuccess();
          }}
          leading={
            <ShoppingBag
              size={16}
              color={semanticColors.action.primary.foreground}
            />
          }
        />
      </DialogActions>
    </>
  );
}

/**
 * Web variant of the Order button. Gets a PaymentIntent from the dashboard API
 * and confirms it in-page with the Stripe payment element (card, plus Apple Pay
 * / Google Pay on supported browsers). No redirect, no native module.
 */
export default function OrderKitButton({ productId }: { productId: string }) {
  const t = useTranslations();
  const stytch = useStytch();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  const startCheckout = async () => {
    setError(false);
    setLoading(true);
    // Start loading Stripe.js now (in parallel with the PaymentIntent fetch)
    // rather than at module load, so the script + telemetry only run for users
    // who actually begin a checkout.
    getStripe();
    const token = stytch.session.getTokens()?.session_jwt;
    const { tenantId } = getIdsFromMetadata(
      stytch.user.getSync()?.trusted_metadata ?? {},
    );
    const secret = await fetchTestingCheckoutSecret({
      token,
      tenantId,
      productId,
    });
    setLoading(false);
    if (!secret) {
      setError(true);
      return;
    }
    setClientSecret(secret);
    setOpen(true);
  };

  return (
    <View>
      {done ? (
        <SecondaryButton
          label={t("learn.test_detail.order_success")}
          onPress={() => {}}
          disabled
          leading={<Check size={16} color={EMBER.colors.text} />}
          style={{ marginTop: 28, opacity: 1 }}
        />
      ) : (
        <PrimaryButton
          label={t("learn.test_detail.order_now")}
          action="learn.test_detail.order_now"
          loading={loading}
          onPress={startCheckout}
          leading={
            <ShoppingBag
              size={16}
              color={semanticColors.action.primary.foreground}
            />
          }
          style={{ marginTop: 28 }}
        />
      )}
      {error ? (
        <FieldMessage centered style={{ marginTop: 8 }}>
          {t("learn.test_detail.order_error")}
        </FieldMessage>
      ) : null}

      <Dialog
        size="wide"
        visible={open}
        onClose={() => setOpen(false)}
        // The checkout had no backdrop dismiss; a stray tap must not drop the
        // card form mid-entry.
        dismissOnBackdropTap={false}
        testID="order-kit-checkout-dialog"
      >
        <CloseButton
          accessibilityLabel={t("learn.article_actions.close_accessibility")}
          action="close_order_kit"
          onPress={() => setOpen(false)}
          style={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}
        />
        {clientSecret ? (
          <Elements stripe={getStripe()} options={{ clientSecret }}>
            <WebCheckoutForm
              onSuccess={() => {
                setOpen(false);
                setDone(true);
              }}
              onError={() => setError(true)}
            />
          </Elements>
        ) : null}
      </Dialog>
    </View>
  );
}
